import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

type SetFootnote = (footnote: ReactNode) => void

const FootnoteSetterContext = createContext<SetFootnote>(() => {})
const FootnoteContext = createContext<ReactNode>(null)

/**
 * Lets a page put a footnote in the site footer (e.g. the prospect page's
 * disclosure) without the footer knowing about pages.
 */
export function SiteFootnoteProvider({ children }: { children: ReactNode }) {
  const [footnote, setFootnote] = useState<ReactNode>(null)
  return (
    <FootnoteSetterContext.Provider value={setFootnote}>
      <FootnoteContext.Provider value={footnote}>{children}</FootnoteContext.Provider>
    </FootnoteSetterContext.Provider>
  )
}

export function useSiteFootnoteContent(): ReactNode {
  return useContext(FootnoteContext)
}

/** Shows `footnote` in the site footer while the calling page is mounted. */
export function useSiteFootnote(footnote: ReactNode, deps: readonly unknown[]) {
  const setFootnote = useContext(FootnoteSetterContext)
  useEffect(() => {
    setFootnote(footnote)
    return () => setFootnote(null)
    // The caller's deps decide when the footnote changes; the node itself is new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFootnote, ...deps])
}
